import {
  Card,
  Grid,
  Group,
  Loader,
  RingProgress,
  SimpleGrid,
  Stack,
  Text,
  Title
} from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api.js";

interface AnalyticsOverview {
  authorizeVolume: number;
  pendingApprovals: number;
  permitIssuance: number;
  gatewayFailures: number;
  connectorErrors: number;
  approvalLatencyP50Ms: number;
  approvalLatencyP95Ms: number;
  activeCustomers: number;
  trialCustomers: number;
}

const StatCard = ({
  label,
  value,
  sub
}: {
  label: string;
  value: string | number;
  sub?: string;
}) => (
  <Card withBorder radius="md" p="md">
    <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>
      {label}
    </Text>
    <Text size="xl" fw={700}>
      {value}
    </Text>
    {sub && (
      <Text size="xs" c="dimmed" mt={2}>
        {sub}
      </Text>
    )}
  </Card>
);

export const AnalyticsPage = () => {
  const { data, isLoading } = useQuery({
    queryKey: ["internal", "analytics", "overview"],
    queryFn: () => api.get<AnalyticsOverview>("/internal/analytics/overview"),
    refetchInterval: 30_000
  });

  if (isLoading || !data) {
    return (
      <Group justify="center" p="xl">
        <Loader />
      </Group>
    );
  }

  const total = data.authorizeVolume;
  const failurePct = total > 0 ? (data.gatewayFailures / total) * 100 : 0;
  const permitPct = total > 0 ? (data.permitIssuance / total) * 100 : 0;

  return (
    <Stack gap="md">
      <Title order={2}>Analytics</Title>

      <SimpleGrid cols={{ base: 2, sm: 3, lg: 4 }} spacing="sm">
        <StatCard
          label="Authorize volume"
          value={data.authorizeVolume.toLocaleString()}
          sub="all time"
        />
        <StatCard label="Pending approvals" value={data.pendingApprovals} />
        <StatCard label="Gateway failures" value={data.gatewayFailures} />
        <StatCard label="Connector errors" value={data.connectorErrors} />
        <StatCard label="Active customers" value={data.activeCustomers} />
        <StatCard label="Trial customers" value={data.trialCustomers} />
        <StatCard
          label="Approval latency p50"
          value={data.approvalLatencyP50Ms > 0 ? `${data.approvalLatencyP50Ms}ms` : "—"}
        />
        <StatCard
          label="Approval latency p95"
          value={data.approvalLatencyP95Ms > 0 ? `${data.approvalLatencyP95Ms}ms` : "—"}
        />
      </SimpleGrid>

      <Grid>
        <Grid.Col span={{ base: 12, sm: 6 }}>
          <Card withBorder radius="md" p="md">
            <Text size="sm" fw={600} mb="md">
              Permit issuance rate
            </Text>
            <Group gap="md" align="center">
              <RingProgress
                size={100}
                thickness={10}
                roundCaps
                sections={[
                  { value: permitPct, color: "teal", tooltip: `${data.permitIssuance} permits` }
                ]}
                label={
                  <Text ta="center" fw={700} size="sm">
                    {Math.round(permitPct)}%
                  </Text>
                }
              />
              <Stack gap={4}>
                <Text size="sm">{data.permitIssuance} permits issued</Text>
                <Text size="xs" c="dimmed">
                  out of {data.authorizeVolume} authorize calls
                </Text>
              </Stack>
            </Group>
          </Card>
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 6 }}>
          <Card withBorder radius="md" p="md">
            <Text size="sm" fw={600} mb="md">
              Gateway failure rate
            </Text>
            <Group gap="md" align="center">
              <RingProgress
                size={100}
                thickness={10}
                roundCaps
                sections={[
                  ...(failurePct > 0
                    ? [
                        {
                          value: failurePct,
                          color: "red",
                          tooltip: `${data.gatewayFailures} failures`
                        }
                      ]
                    : [{ value: 100, color: "teal", tooltip: "No failures" }])
                ]}
                label={
                  <Text ta="center" fw={700} size="sm">
                    {Math.round(failurePct)}%
                  </Text>
                }
              />
              <Stack gap={4}>
                <Text size="sm">{data.gatewayFailures} failures</Text>
                <Text size="xs" c="dimmed">
                  out of {data.authorizeVolume} gateway calls
                </Text>
              </Stack>
            </Group>
          </Card>
        </Grid.Col>
      </Grid>
    </Stack>
  );
};
